import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ChevronRight, ChevronDown, Edit3, X, Maximize2 } from 'lucide-react';

// Recursive component to render JSON nodes
const JsonNode = ({
    nodeKey,
    value,
    level = 0,
    rowId,
    fullPath,
    previewMode,
    editingCell,
    editValue,
    onCellClick,
    onEditValueChange,
    onSaveEdit,
    onCancelEdit,
    shouldUseTextarea
}) => {
    const [isExpanded, setIsExpanded] = useState(level < 2); // Expand first 2 levels by default
    const isObject = value !== null && typeof value === 'object';
    const isArray = Array.isArray(value);
    const isEditing = editingCell?.rowId === rowId && editingCell?.column === fullPath;

    // Left padding based on depth
    const paddingLeft = `${level * 1.5}rem`;

    if (isObject) {
        const keys = Object.keys(value);
        return (
            <div className="font-mono text-sm leading-6">
                <div
                    className="flex items-center cursor-pointer hover:bg-gray-100/50 py-0.5 px-1 rounded transition-colors group"
                    style={{ paddingLeft }}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <span className="w-5 h-5 flex items-center justify-center text-gray-400 group-hover:text-blue-500 mr-1 shrink-0">
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>
                    {nodeKey && <span className="text-purple-600 font-medium mr-2">"{nodeKey}":</span>}
                    <span className="text-gray-500">{isArray ? '[' : '{'}</span>
                    {!isExpanded && <span className="text-gray-400 text-xs px-2 break-normal">... {keys.length} items</span>}
                    {!isExpanded && <span className="text-gray-500">{isArray ? ']' : '}'}</span>}
                </div>

                {isExpanded && (
                    <AnimatePresence>
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden"
                        >
                            {keys.map((k) => (
                                <JsonNode
                                    key={k}
                                    nodeKey={isArray ? null : k}
                                    value={value[k]}
                                    level={level + 1}
                                    rowId={rowId}
                                    fullPath={fullPath ? `${fullPath}.${k}` : k}
                                    previewMode={previewMode}
                                    editingCell={editingCell}
                                    editValue={editValue}
                                    onCellClick={onCellClick}
                                    onEditValueChange={onEditValueChange}
                                    onSaveEdit={onSaveEdit}
                                    onCancelEdit={onCancelEdit}
                                    shouldUseTextarea={shouldUseTextarea}
                                />
                            ))}
                            <div className="py-0.5 px-1 text-gray-500" style={{ paddingLeft: `calc(${paddingLeft} + 1.5rem)` }}>
                                {isArray ? ']' : '}'}
                            </div>
                        </motion.div>
                    </AnimatePresence>
                )}
            </div>
        );
    }

    // Leaf node (primitive value)
    return (
        <div className="font-mono text-sm leading-7 flex relative group/leaf" style={{ paddingLeft: `calc(${paddingLeft} + 1.5rem)` }}>
            {nodeKey && <span className="text-purple-600 font-medium mr-2 shrink-0">"{nodeKey}":</span>}

            {isEditing ? (
                <div className="flex-1 max-w-lg z-10" onClick={e => e.stopPropagation()}>
                    {shouldUseTextarea?.(editValue) ? (
                        <textarea
                            value={editValue}
                            onChange={(e) => onEditValueChange?.(e.target.value)}
                            onBlur={() => onSaveEdit?.()}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.ctrlKey) onSaveEdit?.();
                                else if (e.key === 'Escape') onCancelEdit?.();
                            }}
                            autoFocus
                            className="w-full p-2 border-2 border-blue-400 bg-white rounded-md shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 min-h-[80px] resize-y text-sm font-sans"
                        />
                    ) : (
                        <input
                            type="text"
                            value={editValue}
                            onChange={(e) => onEditValueChange?.(e.target.value)}
                            onBlur={() => onSaveEdit?.()}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') onSaveEdit?.();
                                else if (e.key === 'Escape') onCancelEdit?.();
                            }}
                            autoFocus
                            className="w-full p-1.5 border-2 border-blue-400 bg-white rounded-md shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm font-sans"
                        />
                    )}
                </div>
            ) : (
                <div
                    className={`
                        break-all px-1.5 py-0.5 rounded cursor-pointer transition-colors border border-transparent
                        ${typeof value === 'string' ? 'text-green-600' : typeof value === 'number' ? 'text-blue-600' : typeof value === 'boolean' ? 'text-orange-600' : 'text-gray-400'}
                        ${!previewMode ? 'hover:bg-blue-50 hover:border-blue-200' : ''}
                    `}
                    onClick={() => {
                        if (!previewMode) {
                            onCellClick?.(rowId, fullPath, value);
                        }
                    }}
                >
                    {typeof value === 'string' ? `"${value}"` : String(value)}
                    {!previewMode && (
                        <Edit3 size={12} className="inline-block ml-2 opacity-0 group-hover/leaf:opacity-50 transition-opacity text-gray-500" />
                    )}
                </div>
            )}
        </div>
    );
};

const NestedJSONGrid = ({
    data,
    headers, // We ignore headers for nested JSON, we just unflatten the object
    currentPage,
    rowsPerPage,
    previewMode,
    pendingChanges,
    manualChanges,
    editingCell,
    editValue,
    onRowEdit,
    onCellClick,
    onEditValueChange,
    onSaveEdit,
    onCancelEdit,
    shouldUseTextarea
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    // Re-construct nested objects from flat headers if data came in flat
    const nestedData = useMemo(() => {
        return data.map(row => {
            const nested = { _rowId: row._rowId };
            Object.keys(row).filter(k => k !== '_rowId').forEach(key => {
                // simple unflatten
                const parts = key.split('.');
                let current = nested;
                for (let i = 0; i < parts.length - 1; i++) {
                    const part = parts[i];
                    if (!current[part]) current[part] = {};
                    current = current[part];
                }
                // Try parsing stringified arrays
                let val = row[key];
                if (typeof val === 'string' && (val.trim().startsWith('[') || val.trim().startsWith('{'))) {
                    try { val = JSON.parse(val); } catch (e) { }
                }
                current[parts[parts.length - 1]] = val;
            });
            return nested;
        });
    }, [data]);

    // Filter data based on search term
    const filteredData = useMemo(() => {
        if (!searchTerm.trim()) return nestedData;
        const lowerSearch = searchTerm.toLowerCase();
        return nestedData.filter(row => JSON.stringify(row).toLowerCase().includes(lowerSearch));
    }, [nestedData, searchTerm]);

    // Paginate sorted data
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const currentRows = filteredData.slice(startIndex, endIndex);

    return (
        <div className="flex flex-col h-full bg-slate-50">
            {/* Search Bar */}
            <div className="px-4 py-2 bg-white border-b border-gray-200 flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                        type="text"
                        placeholder="Search JSON objects..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-8 py-1.5 bg-gray-50 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all text-sm"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
                {searchTerm && (
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                        {filteredData.length} of {data.length} records
                    </span>
                )}
            </div>

            {/* JSON List */}
            <div className="flex-1 overflow-auto p-4 space-y-6">
                {currentRows.length > 0 ? (
                    <AnimatePresence mode="popLayout">
                        {currentRows.map((row, index) => {
                            // Strip _rowId for display root
                            const { _rowId, ...displayData } = row;
                            return (
                                <motion.div
                                    key={row._rowId || index}
                                    initial={{ opacity: 0, scale: 0.98 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.98 }}
                                    transition={{ duration: 0.2, delay: index * 0.05 }}
                                    className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
                                >
                                    <div className="bg-slate-100 px-4 py-2 border-b border-gray-200 flex justify-between items-center text-xs">
                                        <div className="font-mono text-slate-500 bg-white px-2 py-1 rounded border border-slate-200 shadow-sm">
                                            Object ID: <span className="font-bold text-slate-700">{row._rowId}</span>
                                        </div>
                                        <button
                                            onClick={() => onRowEdit?.(row)}
                                            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all flex items-center gap-1 font-medium"
                                        >
                                            <Maximize2 size={14} /> Inspect
                                        </button>
                                    </div>
                                    <div className="p-4 bg-slate-50 overflow-x-auto flex flex-col gap-1">
                                        {Object.keys(displayData).map(key => (
                                            <JsonNode
                                                key={key}
                                                nodeKey={key}
                                                value={displayData[key]}
                                                level={0}
                                                rowId={row._rowId}
                                                fullPath={key}
                                                previewMode={previewMode}
                                                editingCell={editingCell}
                                                editValue={editValue}
                                                onCellClick={onCellClick}
                                                onEditValueChange={onEditValueChange}
                                                onSaveEdit={onSaveEdit}
                                                onCancelEdit={onCancelEdit}
                                                shouldUseTextarea={shouldUseTextarea}
                                            />
                                        ))}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                ) : (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                        <Search size={48} className="mb-4 opacity-50" />
                        <p className="text-lg font-medium">No records found</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NestedJSONGrid;
